'use client';

import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { Box } from '@cbhq/cds-web/layout/Box';

import { ErrorNudgeCard } from '../components/ErrorNudgeCard';
import { getErrorVitals } from '../errorVitals';

type ErrorContextType = {
  showError: (message: string, details?: string) => void;
};

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

type ErrorProviderProps = {
  children: ReactNode;
};

export function ErrorProvider({ children }: ErrorProviderProps) {
  const [error, setError] = useState<{ message: string; details?: string } | null>(null);

  const showError = useCallback(
    (message: string, details?: string) => {
      if (error) {
        return;
      }

      const errorToLog = new Error(message);
      if (details) {
        errorToLog.cause = details;
      }

      getErrorVitals()?.logError(errorToLog, {
        context: 'error-banner',
        severity: 'error',
        isHandled: true,
      });

      setError({ message, details });
    },
    [error],
  );

  const hideError = useCallback(() => {
    setError(null);
  }, []);

  const contextValue = useMemo(() => ({ showError }), [showError]);

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
      {error && (
        <Box
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          display="flex"
          justifyContent="center"
          paddingBottom={2}
          paddingStart={1.5}
          paddingEnd={1.5}
          width="100%"
          zIndex={1000}
        >
          <ErrorNudgeCard title={error.message} description={error.details} onDismiss={hideError} />
        </Box>
      )}
    </ErrorContext.Provider>
  );
}

export function useError() {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within ErrorProvider');
  }
  return context;
}
